import { createContext, useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getUser as getDbUser, upsertUser, deleteUser, purgeUsers, setDbName, initDb } from 'services/RxDBService';
import { User } from 'types/user';
import { getLoginUrl, getLogoutUrl, getProfile } from 'utils/api/backend';
import { initializeDayJsLocale } from 'utils/date';
import { useGlobalLoading } from 'hooks/useGlobalLoading';
import { useAppInsights } from 'hooks/useAppInsights';

const LOCAL_STORAGE_UID = 'userId';

interface IProps {
  children: JSX.Element;
}

export const AuthenticationContext = createContext<{
  user: User | undefined;
  loggingIn: boolean;
  loading: boolean;
  error: boolean;
  login: (redirectUrl: string | null) => void;
  logout: () => void;
}>({
  user: { loggedIn: false },
  loggingIn: false,
  loading: false,
  error: false,
  login: () => {},
  logout: () => {}
});

export const AuthenticationContextProvider = ({ children }: IProps): JSX.Element | null => {
  const { setLoadingAuthentication: setGlobalLoadingAuthentication } = useGlobalLoading();
  const appInsights = useAppInsights();
  const [user, setUser] = useState<User | undefined>(undefined);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const login = (redirectUrl: string | null): void => {
    setLoggingIn(true);
    window.location.href = getLoginUrl(redirectUrl);
  };

  const logout = async (): Promise<void> => {
    setLoggingIn(true);

    if (user?.userId) {
      await deleteUser(user?.userId);
    }
    window.localStorage.removeItem(LOCAL_STORAGE_UID);
    window.location.href = getLogoutUrl();
  };

  useEffect(() => {
    setGlobalLoadingAuthentication(loading);
  }, [loading]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const userId = window.localStorage.getItem(LOCAL_STORAGE_UID);
      if (userId && !navigator.onLine) {
        setDbName(userId);
        const dbUser = await getDbUser();

        if (dbUser?.loggedIn) {
          setUser(dbUser);
          setLoggingIn(false);
          setLoading(false);
          // start db replication as soon as we know our used id
          await initDb(queryClient);

          return;
        }
      }

      try {
        const userProfile = await getProfile();

        if (userProfile) {
          userProfile.initials = userProfile.fullName
            ?.split(' ')
            .map((n) => n[0])
            .join('');
          if (userProfile.email) {
            appInsights.appInsights?.setAuthenticatedUserContext(userProfile.email);
          }
          userProfile.loggedIn = true;
          window.localStorage.setItem(LOCAL_STORAGE_UID, userProfile.userId!);
          setDbName(userProfile.userId!);
          initializeDayJsLocale(userProfile.locale);
        }

        setLoading(false);
        setUser(userProfile || undefined);

        if (userProfile) {
          await upsertUser(userProfile);
        } else {
          setUser(undefined);
          await purgeUsers();
        }

        // start db replication as soon as we know our used id
        await initDb(queryClient);
      } catch (e) {
        console.error(e);
        setLoading(false);
        setError(true);
        setUser(undefined);
        setDbName('');
      }
    })();
  }, []);

  const value = useMemo(
    () => ({ user, loggingIn, loading, error, login, logout }),
    [user, loggingIn, loading, error, login, logout]
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};
